/**
 * 📊 DashboardServices — adapter V2
 *
 * Mantém a mesma assinatura usada pelo `DashBoardLayerTen.jsx`
 * (`getDashboardData()` e `getSalesByMonth(period)`) mas alimenta o shape
 * a partir das APIs V2: balancesV2API + salesV2API + productsV2API +
 * withdrawalsV2API. Toda a agregação roda client-side porque o backend V2
 * ainda não expõe endpoints agregados (timeseries, métricas).
 *
 * Gaps conhecidos:
 *  - `commission_amount_cents` por venda NÃO vem em /sales/by-product.
 *    Comissões ficam em `sale_commissions` (split por papel). Para o chart
 *    "Vendas & Comissões" devolvemos `commissionsData: null` e a UI marca
 *    "indisponível" — NÃO usamos heurística (ex: 30% de total) para não
 *    induzir o produtor a erro.
 *  - `logisticsEfficiency`: V2 não trackeia status logístico ainda → 0.
 *  - `semicircleData`: derivado parcialmente das vendas (paid/pending/
 *    cancelled/refunded). After pay vs Antecipado deriva do payment_method.
 *
 * Esses gaps devem ser fechados num endpoint backend
 * `/api/v2/dashboard/overview` numa próxima PR. Por hora, o adapter
 * concentra a lógica num só lugar.
 */

import { balancesV2API } from './v2/withdrawals';
import { salesV2API } from './v2/sales';
import { productsV2API } from './v2/products';
import { withdrawalsV2API } from './v2/withdrawals';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

const sumCents = (sales, predicate) =>
  sales.reduce((acc, s) => (predicate(s) ? acc + (Number(s.total_amount_cents) || 0) : acc), 0);

/**
 * Carrega todas as vendas dos produtos do user autenticado.
 * Retorna array vazio se o user não tem produtos.
 */
async function loadAllSales() {
  const prodRes = await productsV2API.listMine({ limit: 500 });
  const products = prodRes.data?.products || [];
  if (products.length === 0) return [];

  const salesByProduct = await Promise.all(
    products.map((p) =>
      salesV2API.listByProduct(p.id, { limit: 200 }).catch(() => ({ data: { sales: [] } })),
    ),
  );
  return salesByProduct.flatMap((r) => r.data?.sales || []);
}

/**
 * Carrega saques (withdrawals) do user para somar `totalWithdrawals`.
 * Lista todos os status "paid" e soma o `amount_cents`.
 */
async function loadTotalWithdrawalsCents() {
  try {
    const res = await withdrawalsV2API.listMine({ status: 'paid', limit: 200 });
    const withdrawals = res.data?.withdrawals || [];
    return withdrawals.reduce((acc, w) => acc + (Number(w.amount_cents) || 0), 0);
  } catch {
    return 0;
  }
}

/**
 * Distribuição por payment_method (vendas paid) no formato esperado pelo V1:
 *   [{ payment_method: 'credit_card', count: 12 }, ...]
 */
function buildSalesByPaymentMethod(sales) {
  const dist = {};
  for (const s of sales) {
    if (s.payment_status !== 'paid') continue;
    const m = s.payment_method || 'other';
    dist[m] = (dist[m] || 0) + 1;
  }
  return Object.entries(dist).map(([payment_method, count]) => ({ payment_method, count }));
}

/**
 * Semicirculares (Balanço Geral):
 *   - afterpay (esquerdo)  → vendas com payment_method = 'afterpay' (ou boleto pago só na entrega)
 *   - antecipado (direito) → demais (credit_card, pix, etc — pagam antes da entrega)
 * Como V2 não rastreia ainda "em trânsito"/"frustrado" no schema, a UI lê 0 nesses slots.
 */
function buildSemicircleData(sales) {
  const afterPayMethods = new Set(['afterpay', 'after_pay', 'cod']); // cash on delivery
  const acc = {
    afterpay: { pedidos_pagos: 0, pedidos_transito: 0, pagamentos_pendentes: 0, pedidos_frustrados: 0, total: 0 },
    antecipado: { pagamento_aprovado: 0, pagamentos_pendentes: 0, cancelados: 0, reembolsados: 0, total: 0 },
  };
  for (const s of sales) {
    const isAfter = afterPayMethods.has(s.payment_method);
    const target = isAfter ? acc.afterpay : acc.antecipado;
    target.total += 1;
    if (isAfter) {
      if (s.payment_status === 'paid') target.pedidos_pagos += 1;
      else if (s.payment_status === 'pending') target.pagamentos_pendentes += 1;
      else if (s.payment_status === 'cancelled' || s.payment_status === 'refunded') target.pedidos_frustrados += 1;
    } else {
      if (s.payment_status === 'paid') target.pagamento_aprovado += 1;
      else if (s.payment_status === 'pending') target.pagamentos_pendentes += 1;
      else if (s.payment_status === 'cancelled') target.cancelados += 1;
      else if (s.payment_status === 'refunded') target.reembolsados += 1;
    }
  }
  return acc;
}

/**
 * Faturamento semanal (últimos 7 dias, Dom → Sáb da semana corrente).
 * `withdrawals` por dia não está disponível ainda em V2 (precisa endpoint
 * agregado) → preenchemos só `revenue` e deixamos `withdrawals` em 0.
 */
function buildWeeklyBillingData(sales) {
  const now = new Date();
  const dow = now.getDay(); // 0 = Dom
  const weekStart = startOfDay(new Date(now.getTime() - dow * 86400000));
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getTime() + i * 86400000);
    return { date: d, revenue: 0, withdrawals: 0 };
  });
  for (const s of sales) {
    if (s.payment_status !== 'paid') continue;
    const dt = new Date(s.created_at);
    const idx = days.findIndex((d) => isSameDay(d.date, dt));
    if (idx >= 0) days[idx].revenue += (Number(s.total_amount_cents) || 0);
  }
  return days;
}

/**
 * Vendas recentes (8 últimas, qualquer status). Mantém o shape esperado pela
 * tabela V1: { id, customer_name, customer_email, product_name, total_amount,
 * status, created_at }.
 */
function buildRecentSales(sales) {
  return [...sales]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8)
    .map((s) => ({
      id: s.id,
      customer_name: s.customer_name || 'Cliente não identificado',
      customer_email: s.customer_email || '',
      product_name: s.product_name || `Produto #${s.product_id}`,
      total_amount: Number(s.total_amount_cents) || 0,
      status: mapPaymentStatusToV1(s.payment_status),
      created_at: s.created_at,
    }));
}

function mapPaymentStatusToV1(status) {
  // V1 esperava: 'completed' | 'pending' | 'processing' | 'cancelled'
  switch (status) {
    case 'paid': return 'completed';
    case 'pending': return 'pending';
    case 'processing': return 'processing';
    case 'cancelled':
    case 'refunded':
      return 'cancelled';
    default: return 'pending';
  }
}

const DashboardServices = {
  /**
   * Constrói o objeto `dashboardData` esperado pelo V1, a partir de V2.
   * Retorno: { success: true, data: { todaySales, yesterdaySales, totalBalance,
   *   availableBalance, pendingBalance, anticipationBalance, totalSales,
   *   monthSales, semicircleData, logisticsEfficiency, salesByPaymentMethod,
   *   totalRevenue, totalWithdrawals, weeklyBillingData, recentSales } }
   */
  getDashboardData: async () => {
    try {
      const [balRes, sales, totalWithdrawalsCents] = await Promise.all([
        balancesV2API.me().catch(() => ({ data: { balance: {} } })),
        loadAllSales(),
        loadTotalWithdrawalsCents(),
      ]);

      const b = balRes.data?.balance || {};
      const availableCents = Number(b.available_cents) || 0;
      const pendingCents = Number(b.pending_cents) || 0;
      const advanceCents = Number(b.advance_anticipated_cents) || 0;

      const now = new Date();
      const yesterday = new Date(now.getTime() - 86400000);
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const todayCents = sumCents(sales, (s) => s.payment_status === 'paid' && isSameDay(s.created_at, now));
      const yesterdayCents = sumCents(sales, (s) => s.payment_status === 'paid' && isSameDay(s.created_at, yesterday));
      const monthCents = sumCents(
        sales,
        (s) => s.payment_status === 'paid' && new Date(s.created_at) >= startOfMonth,
      );
      const totalCents = sumCents(sales, (s) => s.payment_status === 'paid');
      const totalCount = sales.length;

      return {
        success: true,
        data: {
          todaySales: { amount: todayCents, count: sales.filter((s) => s.payment_status === 'paid' && isSameDay(s.created_at, now)).length },
          yesterdaySales: { amount: yesterdayCents },
          monthSales: { amount: monthCents },
          totalSales: { count: totalCount, amount: totalCents },

          // Saldos (centavos)
          totalBalance: availableCents + pendingCents + advanceCents,
          availableBalance: availableCents,
          pendingBalance: pendingCents,
          anticipationBalance: advanceCents,

          // Faturamento
          totalRevenue: totalCents,
          totalWithdrawals: totalWithdrawalsCents,
          weeklyBillingData: buildWeeklyBillingData(sales),

          // Charts
          salesByPaymentMethod: buildSalesByPaymentMethod(sales),
          semicircleData: buildSemicircleData(sales),

          // Logística: V2 ainda não trackeia status de entrega → indisponível (0)
          logisticsEfficiency: { completed: 0, pending: 0, frustrated: 0, cancelled: 0 },

          // Vendas recentes
          recentSales: buildRecentSales(sales),

          // Para uso interno do componente (chart timeseries)
          _allSalesV2: sales,
        },
      };
    } catch (error) {
      console.error('DashboardServices(V2) - Erro ao montar dashboard:', error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao buscar dados do dashboard');
    }
  },

  /**
   * Série temporal de vendas por mês. Agrega a partir das vendas V2.
   * Período: '3months' | '6months' | '12months'.
   *
   * IMPORTANTE — comissões: V2 não retorna `commission_amount_cents` em
   * /sales/by-product. Para não exibir valor errado no chart,
   * `commissionsData` vem como `null`. A UI deve exibir "indisponível" no
   * dataset de comissões em vez de heurística (ex: 30% de total).
   *
   * TODO backend: endpoint /api/v2/sales/timeseries que agregue
   * commission_amount_cents reais (somando sale_commissions onde
   * participant_user_id = userId logado) por mês.
   */
  getSalesByMonth: async (period = '6months', precomputedSales = null) => {
    try {
      const sales = precomputedSales || (await loadAllSales());
      const monthsCount = period === '3months' ? 3 : period === '12months' ? 12 : 6;
      const now = new Date();
      const buckets = [];
      for (let i = monthsCount - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({
          label: MONTH_LABELS[d.getMonth()],
          year: d.getFullYear(),
          month: d.getMonth(),
          salesCents: 0,
        });
      }
      for (const s of sales) {
        if (s.payment_status !== 'paid') continue;
        const dt = new Date(s.created_at);
        const b = buckets.find((x) => x.year === dt.getFullYear() && x.month === dt.getMonth());
        if (!b) continue;
        b.salesCents += (Number(s.total_amount_cents) || 0);
      }
      return {
        success: true,
        data: {
          months: buckets.map((b) => b.label),
          // V1 esperava reais; manter em reais (cents/100) para compat com axis/tooltip do chart
          salesData: buckets.map((b) => Math.round(b.salesCents / 100)),
          // null = "indisponível" — UI esconde dataset de comissões com fallback explícito
          commissionsData: null,
          commissionsUnavailable: true,
        },
      };
    } catch (error) {
      console.error('DashboardServices(V2) - Erro ao montar série de vendas mensais:', error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao buscar vendas mensais');
    }
  },

  /**
   * @deprecated Mantido apenas para retro-compatibilidade. V2 não tem
   * endpoint equivalente; lança erro pra forçar remoção das chamadas.
   */
  seedHistoricalData: async () => {
    throw new Error('seedHistoricalData removido na migração V2.');
  },
};

export default DashboardServices;
